import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { ArrowRight, ArrowLeft, BookOpen, Clock, CalendarCheck } from "lucide-react";
import Nav from "../site/Nav";
import SEO, { SITE_URL } from "../site/SEO";
import Footer from "../site/Footer";
import ReadingProgress from "../site/ReadingProgress";
import useScrollReveal from "../../hooks/useScrollReveal";
import {
  ARTICLES,
  getArticleBySlug,
  getCategory,
  getRelatedArticles,
} from "../../data/insights";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

/** Build a TOC from the article's H2 headings. Slugifies the same way
 *  `react-markdown` does (lowercase, hyphenated) so the anchor clicks work. */
const slugifyHeading = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const extractH2s = (markdown = "") => {
  const out = [];
  const re = /^##\s+(.+?)\s*$/gm;
  let m;
  while ((m = re.exec(markdown))) {
    const text = m[1].trim();
    out.push({ text, id: slugifyHeading(text) });
  }
  return out;
};

function ArticleJsonLd({ article }) {
  const cat = getCategory(article.category);
  const canonical = `${SITE_URL}/insights/${article.slug}`;
  const graph = [
    {
      "@type": "Article",
      "@id": `${canonical}#article`,
      headline: article.title,
      description: article.description,
      datePublished: article.publishedAt,
      dateModified: article.updatedAt || article.publishedAt,
      mainEntityOfPage: canonical,
      author: {
        "@type": "Organization",
        name: article.author?.name || "The Modern Exit",
        url: SITE_URL,
      },
      publisher: {
        "@type": "Organization",
        name: "The Modern Exit",
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.svg` },
      },
      articleSection: cat?.label || "Insights",
      keywords: (article.tags || []).map((t) => getCategory(t)?.label || t).join(", "),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_URL}/insights` },
        { "@type": "ListItem", position: 3, name: article.title, item: canonical },
      ],
    },
  ];
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({ "@context": "https://schema.org", "@graph": graph })}
      </script>
    </Helmet>
  );
}

function Toc({ items }) {
  if (!items.length) return null;
  return (
    <nav
      data-testid="insight-toc"
      aria-label="Table of contents"
      className="rounded-3xl border border-sand-300 bg-sand-50 p-6 sm:p-7"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-clay">
        On this page
      </p>
      <ol className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              className="flex items-start gap-2.5 text-[13.5px] leading-snug text-ink-muted hover:text-clay transition-colors"
            >
              <span className="font-mono text-[11px] text-clay tabular-nums w-5 pt-0.5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{it.text}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function InlineCta() {
  return (
    <aside
      data-testid="insight-inline-cta"
      className="my-10 rounded-3xl border border-clay/30 bg-clay/5 p-7 sm:p-8"
    >
      <p className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-clay">
        Stop reading. Start fixing.
      </p>
      <h3 className="font-display mt-3 text-2xl sm:text-[1.7rem] leading-tight tracking-tight font-semibold text-ink">
        Want us to map the gap between your traffic and your booked revenue?
      </h3>
      <p className="mt-3 text-[14.5px] text-ink-muted leading-relaxed max-w-2xl">
        Apply for a thirty-minute Revenue Audit. No slides, no pitch — just the
        specific leak in your current flow and the build that plugs it.
      </p>
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("open-audit", { detail: {} }))}
          data-testid="insight-cta-audit"
          data-magnetic
          className="inline-flex items-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-3 text-[14px] font-medium hover:bg-rust transition-colors min-h-[44px]"
        >
          Apply for a Revenue Audit <ArrowRight size={14} />
        </button>
        <Link
          to="/packages"
          data-testid="insight-cta-packages"
          className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-transparent text-clay px-5 py-3 text-[14px] font-medium hover:bg-clay hover:text-sand-100 transition-colors min-h-[44px]"
        >
          View Packages <ArrowRight size={14} />
        </Link>
      </div>
    </aside>
  );
}

function FinalCta() {
  return (
    <section
      data-testid="insight-final-cta"
      className="mt-20 rounded-3xl bg-ink-deep text-sand-100 p-8 sm:p-12"
    >
      <div className="grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-7">
          <p className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-clay">
            Next move
          </p>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-[2.5rem] leading-[1.05] tracking-[-0.02em] font-semibold">
            Pick the path that matches the leak you just read about.
          </h2>
        </div>
        <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
          <Link
            to="/websites"
            data-testid="insight-cta-final-websites"
            className="inline-flex items-center justify-between gap-2 rounded-full bg-sand-100 text-ink px-6 py-3.5 text-[14px] font-medium hover:bg-clay hover:text-sand-100 transition-colors min-h-[48px]"
          >
            Website Packages <ArrowRight size={15} />
          </Link>
          <Link
            to="/revenue-systems"
            data-testid="insight-cta-final-revenue"
            className="inline-flex items-center justify-between gap-2 rounded-full border border-sand-300/30 text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-sand-100/10 transition-colors min-h-[48px]"
          >
            Revenue Systems <ArrowRight size={15} />
          </Link>
          <Link
            to="/build-brief"
            data-testid="insight-cta-final-brief"
            data-magnetic
            className="inline-flex items-center justify-between gap-2 rounded-full bg-clay text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-rust transition-colors min-h-[48px]"
          >
            Start My Website Build <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Custom renderer so that our H2s get anchor IDs for the TOC. */
const markdownComponents = {
  h2: ({ children, ...props }) => {
    const text = String(children);
    return (
      <h2 id={slugifyHeading(text)} {...props}>
        {children}
      </h2>
    );
  },
};

export default function InsightArticlePage() {
  useScrollReveal();
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const toc = useMemo(() => extractH2s(article?.body), [article]);
  const related = useMemo(() => getRelatedArticles(slug, 3), [slug]);

  if (!article) {
    // Unknown slug → punt to the Insights index.
    return <Navigate to="/insights" replace />;
  }

  const cat = getCategory(article.category);
  const canonical = `${SITE_URL}/insights/${article.slug}`;

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="insight-article-page">
      <SEO
        title={`${article.title} · The Modern Exit`}
        description={article.description}
        path={`/insights/${article.slug}`}
      />
      <Helmet>
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:modified_time" content={article.updatedAt || article.publishedAt} />
        <meta property="article:author" content={article.author?.name || "The Modern Exit"} />
        <meta property="article:section" content={cat?.label || "Insights"} />
      </Helmet>
      <ArticleJsonLd article={article} />
      <ReadingProgress />
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb + back */}
          <nav aria-label="Breadcrumb" className="reveal">
            <ol className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink-muted">
              <li>
                <Link to="/" className="hover:text-clay transition-colors">Home</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/insights" className="hover:text-clay transition-colors">Insights</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-clay truncate max-w-[240px] sm:max-w-none">
                {cat?.label || "Article"}
              </li>
            </ol>
          </nav>

          {/* Title + meta */}
          <header className="reveal mt-6 max-w-4xl">
            {cat && (
              <p className="eyebrow inline-flex items-center gap-2 !text-clay">
                <BookOpen size={13} /> {cat.label}
              </p>
            )}
            <h1 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              {article.title}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-ink-muted leading-relaxed">
              {article.description}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-muted">
              <span>By {article.author?.name || "The Modern Exit"}</span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarCheck size={11} /> Published {fmtDate(article.publishedAt)}
              </span>
              {article.updatedAt && article.updatedAt !== article.publishedAt && (
                <span>Updated {fmtDate(article.updatedAt)}</span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} /> {article.readingTime} min read
              </span>
            </div>
          </header>

          {/* Body layout: article + sticky TOC sidebar */}
          <div className="mt-12 grid lg:grid-cols-[1fr_280px] gap-12 items-start">
            <article
              data-testid="insight-article-body"
              className="reveal prose prose-sm sm:prose-base max-w-none text-ink
                prose-headings:font-display prose-headings:tracking-tight prose-headings:text-ink
                prose-h2:text-[1.55rem] sm:prose-h2:text-[1.75rem] prose-h2:mt-12 prose-h2:mb-4 prose-h2:scroll-mt-28 prose-h2:font-semibold
                prose-h3:text-[1.15rem] prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-[15.5px] prose-p:leading-[1.75] prose-p:text-ink
                prose-li:text-[15.5px] prose-li:leading-[1.7] prose-li:text-ink
                prose-strong:text-ink prose-strong:font-semibold
                prose-a:text-clay hover:prose-a:text-rust prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-l-clay prose-blockquote:text-ink-muted
                prose-code:text-clay prose-code:bg-sand-200 prose-code:px-1.5 prose-code:rounded prose-code:text-[13px]"
            >
              <ReactMarkdown components={markdownComponents}>
                {article.body}
              </ReactMarkdown>
              <InlineCta />
            </article>

            <aside className="hidden lg:block sticky top-28 self-start space-y-4 reveal">
              <Toc items={toc} />
              <Link
                to="/insights"
                data-testid="insight-back-link"
                className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.16em] text-clay hover:text-rust transition-colors"
              >
                <ArrowLeft size={12} /> Back to insights
              </Link>
            </aside>
          </div>

          {/* Final CTA */}
          <FinalCta />

          {/* Related */}
          {related.length > 0 && (
            <section
              data-testid="insight-related"
              className="mt-20"
            >
              <p className="eyebrow !text-clay">More insights</p>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl leading-tight tracking-tight font-semibold text-ink">
                Keep reading
              </h2>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {related.map((a) => {
                  const rc = getCategory(a.category);
                  return (
                    <Link
                      key={a.slug}
                      to={`/insights/${a.slug}`}
                      data-testid={`insight-related-${a.slug}`}
                      className="group flex flex-col rounded-3xl border border-sand-300 bg-sand-50 p-7 hover:border-clay/40 transition-colors"
                    >
                      {rc && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
                          {rc.label}
                        </span>
                      )}
                      <h3 className="font-display mt-3 text-xl leading-tight font-semibold text-ink group-hover:text-rust transition-colors">
                        {a.title}
                      </h3>
                      <p className="mt-3 text-[13.5px] text-ink-muted leading-relaxed flex-1">
                        {a.description}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Fallback back link for non-sticky (mobile) */}
          <div className="mt-16 reveal lg:hidden">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.16em] text-clay hover:text-rust"
            >
              <ArrowLeft size={12} /> Back to insights
            </Link>
          </div>

          {/* Unused articles var to satisfy lint + future-proof imports */}
          <span className="sr-only" aria-hidden="true">
            {ARTICLES.length} total articles
          </span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
